import { TrafficShareWithVisits } from "app/components/React/Table/cells";
import { i18nFilter } from "app/filters/i18nFilter";
import { abbrNumberVisitsFilter, minVisitsAbbrFilter, percentageSignFilter } from "app/filters/ngFilters";
import KeywordsTableDashboardDataFetcher from "app/pages/website-analysis/traffic-sources/search/KeywordsTableDashboardDataFetcher";
import CountryService from "app/services/CountryService";
import DurationService from "app/services/DurationService";
import { DefaultCellRightAlign } from "../../React/Table/cells/DefaultCellRightAlign";
import { DefaultCellHeaderRightAlign } from "../../React/Table/headerCells/DefaultCellHeaderRightAlign";
import { SearchTableWithHeaderCompareTemplateV2 } from "../dashboard/SearchTableWithHeaderCompareTemplate-v2";
import { SearchTableWithHeaderSingleTemplateV2 } from "../dashboard/SearchTableWithHeaderSingleTemplate-v2";
import type { IDataFetcherFactory } from "../widget-fetchers/IDataFetcher";
import { TableSearchKeywordsDashboardWidgetFilters } from "../widget-types/TableSearchKeywordsDashboardWidgetFilters";
import { TableWidget } from "./TableWidget";
import widgetSettings from "app/components/dashboard/WidgetSettings";

export class TableSearchKeywordsDashboardWidget extends TableWidget {
    constructor() {
        super();
    }

    public getSearchKey() {
        return "SearchTerm";
    }

    runWidget() {
        if (this._autoFetchData) {
            this.getData();
        }
        this.setMetadata();
        this.getLegendImage();
    }

    static getWidgetMetadataType() {
        return "TableSearchKeywordsDashboard";
    }

    static getWidgetDashboardType() {
        return "Table";
    }

    static getWidgetResourceType() {
        return "Table";
    }

    static getFiltersComponent() {
        return TableSearchKeywordsDashboardWidgetFilters;
    }

    get templateUrl() {
        return this.isCompare()
            ? `/app/components/widget/widget-templates/search-table-with-header-compare.html`
            : `/app/components/widget/widget-templates/search-table-with-header-single.html`;
    }
    get templateComponent() {
        return this.isCompare() ? SearchTableWithHeaderCompareTemplateV2 : SearchTableWithHeaderSingleTemplateV2;
    }
    private getSearchMode() {
        let searchMode = "Organic";
        const { IncludeOrganic, IncludePaid } = this.apiParams;
        if (IncludePaid && !IncludeOrganic) {
            searchMode = "Paid";
        }
        return searchMode;
    }

    protected rowReducer(row) {
        const requestParams = this._params;
        const state = widgetSettings.getMetricProperties(requestParams.metric).state;
        const stateParams = {
            isWWW: "*",
            duration: DurationService.getDiffSymbol(
                requestParams.from,
                requestParams.to,
                requestParams.isWindow ? "days" : "months",
            ),
            country: requestParams.country,
            keyword: row.SearchTerm,
        };
        return {
            ...row,
            url: this._swNavigator.href(state, stateParams, {}),
        };
    }

    getCompareBarsItems() {
        return [
            {
                bars: (this.getWidgetModel().key as any).map((website, i) => {
                    return {
                        color: this.tableColors._available[i],
                        valueText:
                            this.subtitleData &&
                            this.subtitleData.breakdown.search_visits[website.name] &&
                            `${(this.subtitleData.breakdown.search_visits[website.name].percentage * 100).toFixed(2)}%`,
                        width:
                            this.subtitleData &&
                            this.subtitleData.breakdown.search_visits[website.name] &&
                            this.subtitleData.breakdown.search_visits[website.name].percentage * 100,
                        name: website.name,
                    };
                }),
                metric: "analysis.source.search.keywords.header.metrics.searchVisits",
            },
        ];
    }

    getSingleHeader = () => this.subtitleData;

    getSingleHeaderItem = (attr) => this.subtitleData && this.subtitleData.total[attr];

    getAbbrNumberVisitsFilter = () => abbrNumberVisitsFilter();

    getMinVisitsAbbrFilter = () => minVisitsAbbrFilter();

    getPercentagesignFilter = () => percentageSignFilter();

    getI18nFilter = () => i18nFilter();

    getColumnsConfig() {
        const compare = this.isCompare();
        const i18n = this.getI18nFilter();
        const { country, IncludeOrganic, IncludePaid, webSource } = this.apiParams;
        const isUsState = CountryService.isUSState(country);
        const COLUMNS_I18N_KEY = isUsState
            ? "analysis.source.search.all.table.columns.us"
            : "analysis.source.search.all.table.columns";
        const OrganicPaidFilter = this.getSearchMode();
        const includeOrganicPaidColumn =
            webSource === "Desktop" && ((IncludeOrganic && IncludePaid) || (!IncludeOrganic && !IncludePaid));

        const columns = [
            {
                name: "SearchTerm",
                title: "Search Term",
                cellTemp: "cell-keyword-dashboard",
                width: "150px",
            },
            {
                name: "TotalShare",
                title: i18n("analysis.source.search.all.table.columns.share.title"),
                cellComponent: TrafficShareWithVisits,
                width: "120px",
                format: "percentagesign",
                ppt: {
                    overrideFormat: "smallNumbersPercentage:2",
                },
            },
            compare
                ? {
                      name: "SiteOrigins",
                      title: i18n("analysis.source.search.all.table.columns.shareCompare.title"),
                      cellTemp: "group-traffic-share-dashboard",
                      width: "185px",
                      ppt: {
                          // Indicates that we want to split this column into sub-columns
                          // where each sub-column will be a table entity (table item - a website/app/category.
                          // such as ynet.co.il, whatsapp, all industries etc.)
                          splitColumnToEntities: true,

                          // override the table column format when rendered in ppt
                          overrideFormat: "smallNumbersPercentage:1",

                          // Override default value when no value is present in a table roe
                          defaultValue: "0%",
                      },
                  }
                : {
                      name: "Change",
                      title: i18n("analysis.source.search.all.table.columns.change.title"),
                      cellTemp: "change-percentage",
                      headerComponent: DefaultCellHeaderRightAlign,
                      format: "percentagesign",
                      width: "100px",
                      ppt: {
                          overrideFormat: "smallNumbersPercentage:2",
                      },
                  },
            {
                name: "KwVolume",
                title: `${COLUMNS_I18N_KEY}.volume.title`,
                format: "swPosition",
                headerComponent: DefaultCellHeaderRightAlign,
                cellComponent: DefaultCellRightAlign,
                width: "100px",
            },
            {
                name: "CPC",
                title: `${COLUMNS_I18N_KEY}.cpc.title`,
                cellComponent: DefaultCellRightAlign,
                headerComponent: DefaultCellHeaderRightAlign,
                format: "CPC",
                width: "60px",
            },
            {
                name: `Position${OrganicPaidFilter}`,
                title: `${i18n(
                    `${COLUMNS_I18N_KEY}.position${OrganicPaidFilter.toLowerCase()}.title`,
                )} (${OrganicPaidFilter})`,
                cellTemp: compare ? "wa-keyword-position-compare" : "wa-keyword-position",
                headerComponent: DefaultCellHeaderRightAlign,
                format: "avgKeywordPosition",
                width: "130px",
                ppt: {
                    // hide this column since it's impossible to format its data
                    hideColumn: true,
                },
            },
            {
                name: `DestUrl${OrganicPaidFilter}`,
                trackingName: `URL ${OrganicPaidFilter}`,
                title: `${i18n(
                    `${COLUMNS_I18N_KEY}.destination${OrganicPaidFilter.toLowerCase()}.title`,
                )} (${OrganicPaidFilter})`,
                cellTemp: compare ? "wa-search-keyword-url-compare" : "wa-search-keyword-url",
                minWidth: 200,
                ppt: {
                    // hide this column since it's impossible to format its data
                    hideColumn: true,
                },
            },
        ];

        if (includeOrganicPaidColumn) {
            const columnDetails: any = {
                name: "Organic",
                title: "analysis.source.search.all.table.columns.vs.title",
                cellTemp: "organic-paid",
                width: "190px",
                ppt: {
                    // Indicates that we want to replace this column with two other columns
                    // (show the specified columns instead of the current column)
                    // this is done mainly in cases of Vs. columns, where we want to split
                    // the column into two ppt columns (such as: "Organic Vs Paid" >>> "Organic", "Paid")
                    replaceWithColumns: ["Organic", "Paid"],

                    // override the table column format when rendered in ppt
                    overrideFormat: "smallNumbersPercentage:2",
                },
            };
            columns.splice(3, 0, columnDetails);
        }

        return columns;
    }

    public getFetcherFactory() {
        return new KeywordsTableDashboardDataFetcher() as IDataFetcherFactory<any>;
    }
}

TableSearchKeywordsDashboardWidget.register();
